import {
  ActionIcon,
  Group,
  type GroupProps,
  Menu,
  Tooltip
} from "@mantine/core";
import { IconCalendarStar } from "@tabler/icons-react";
import type { DateTime } from "luxon";
import { useState } from "react";
import { THEME_VARS } from "../../../utils/constants";
import { DateInput, type DateInputProps } from "../DateInput/DateInput";
import {
  DateRangePresets,
  getPresetMonths,
  getPresetYears,
  getPresetYearsAndMonths
} from "./DateRangeInput.constants";
import { getEndMaximum } from "./utils/getEndMaximum";
import { getEndMinimum } from "./utils/getEndMinimum";
import { getStartMaximum } from "./utils/getStartMaximum";
import { getStartMinimum } from "./utils/getStartMinimum";

type SingleDateRangeValue = DateInputProps["value"] | undefined;
type DateInputOnChangeValue = [SingleDateRangeValue, SingleDateRangeValue];
type OnChangeFn = (value: DateInputOnChangeValue) => void;
type ReducedDateInputProps = Omit<
  DateInputProps,
  "value" | "defaultValue" | "onChange"
>;

interface DateRangeInputProps extends Omit<
  GroupProps,
  "defaultValue" | "value" | "onChange"
> {
  value?: DateInputOnChangeValue;
  disabled?: boolean;
  hasError?: boolean;
  name?: DateInputProps["name"];
  onChange?: OnChangeFn;
  minDate?: DateInputProps["minDate"];
  maxDate?: DateInputProps["maxDate"];
  maxRangeInDays?: number;
  presets?: {
    type: keyof typeof DateRangePresets;
    start?: DateTime;
    end?: DateTime;
  };
  startInputProps?: ReducedDateInputProps;
  endInputProps?: ReducedDateInputProps;
}

function DateRangeInput({
  value,
  disabled,
  hasError,
  name,
  onChange,
  minDate,
  maxDate,
  maxRangeInDays,
  presets,
  startInputProps,
  endInputProps,
  ...groupProps
}: DateRangeInputProps) {
  // value in state so you can use the component controlled or uncontrolled
  const [tempValue, setTempValue] = useState<DateInputOnChangeValue>(
    value || [null, null]
  );

  const presetsItems =
    presets?.type === DateRangePresets.Years
      ? getPresetYears({
          startYear: presets.start?.year,
          endYear: presets.end?.year,
          onChange: (val) => {
            setTempValue(val);
            onChange?.(val);
          }
        })
      : presets?.type === DateRangePresets.Months
        ? getPresetMonths({
            startMonth: presets.start,
            endMonth: presets.end,
            onChange: (val) => {
              setTempValue(val);
              onChange?.(val);
            }
          })
        : presets?.type === DateRangePresets.YearsAndMonths
          ? getPresetYearsAndMonths({
              startMonth: presets.start,
              endMonth: presets.end,
              onChange: (val) => {
                setTempValue(val);
                onChange?.(val);
              }
            })
          : [];

  function handleChangeStart(newStart: DateTime | null) {
    setTempValue([newStart, tempValue[1]]);
    onChange?.([newStart, tempValue[1]]);
  }

  function handleChangeEnd(newEnd: DateTime | null) {
    setTempValue([tempValue[0], newEnd]);
    onChange?.([tempValue[0], newEnd]);
  }

  const minStartValue = getStartMinimum({
    tempEnd: tempValue[1],
    startInputPropMin: startInputProps?.minDate,
    globalMin: minDate,
    maxRangeInDays
  });

  const maxStartValue = getStartMaximum({
    tempEnd: tempValue[1],
    startInputPropMax: startInputProps?.maxDate
  });

  const minEndValue = getEndMinimum({
    tempStart: tempValue[0],
    endInputPropMin: endInputProps?.minDate
  });

  const maxEndValue = getEndMaximum({
    tempStart: tempValue[0],
    endInputPropMax: endInputProps?.maxDate,
    globalMax: maxDate,
    maxRangeInDays
  });

  return (
    <Group {...groupProps}>
      <DateInput
        {...startInputProps}
        disabled={disabled || startInputProps?.disabled}
        error={startInputProps?.error || hasError}
        maxDate={maxStartValue}
        minDate={minStartValue}
        name={startInputProps?.name || (name ? `${name}-start` : "start")}
        placeholder={startInputProps?.placeholder || "Startdatum"}
        value={tempValue[0]}
        w={startInputProps?.w || 125}
        onChange={handleChangeStart}
      />
      <span>–</span>
      <DateInput
        {...endInputProps}
        disabled={disabled || endInputProps?.disabled}
        error={endInputProps?.error || hasError}
        maxDate={maxEndValue}
        minDate={minEndValue}
        name={endInputProps?.name || (name ? `${name}-end` : "end")}
        placeholder={endInputProps?.placeholder || "Enddatum"}
        value={tempValue[1]}
        w={endInputProps?.w || 125}
        onChange={handleChangeEnd}
      />
      {presetsItems.length > 0 && (
        <Menu>
          <Menu.Target>
            <Tooltip label="Vordefinierte Zeiträume">
              <ActionIcon
                color={THEME_VARS.neutral[6]}
                title="Vordefinierte Zeiträume"
                variant="transparent"
              >
                <IconCalendarStar />
              </ActionIcon>
            </Tooltip>
          </Menu.Target>
          <Menu.Dropdown mah={400} style={{ overflowY: "auto" }}>
            {presetsItems.map((preset) => (
              <Menu.Item key={preset.label} onClick={preset.onClick}>
                {preset.label}
              </Menu.Item>
            ))}
          </Menu.Dropdown>
        </Menu>
      )}
    </Group>
  );
}

export {
  DateInputOnChangeValue,
  DateRangeInput,
  DateRangeInputProps,
  SingleDateRangeValue
};
